import LogRocket from 'logrocket';

import { toast } from '@utilities';
import { narrativeStatus } from '@helpers/narratives';

import {
  VizGroupedHorizontalBar,
  VizGroupedVerticalBar,
  VizHorizontalBar,
  VizLineChart,
  VizScatter,
  VizSimpleTree,
  VizStackedHorizontalBar,
  VizSummaryCards,
  VizTable,
  VizTornado,
  VizTree,
  VizVerticalBar,
} from '@utilities/icons/narrative';
import { filterPrompts } from '../../Narratives/components/ModalBuildNarrative/utilities/helpers';
import {
  DEFAULT_DRAFT_NARRATIVE_NAME,
  MAX_CHAT_MESSAGE_LENGTH,
  MAX_TOPIC_CARDS,
} from './constants';

const DEFAULT_PROMPT_NAME = 'Narrative Prompt';

export const EMPTY_DOWNLOAD_MAP = new Map();

const isDefined = (value) => value !== undefined && value !== null;
const normalizeId = (value) => (isDefined(value) ? String(value) : value);

export const areSameOrderedIds = (previousIds = [], nextIds = []) => {
  const isSameLength = previousIds.length === nextIds.length;
  return (
    isSameLength &&
    previousIds.every((id, index) => normalizeId(id) === normalizeId(nextIds[index]))
  );
};

export const mergeTopicIds = (...topicIdGroups) => {
  const normalizedKeyToId = new Map();

  topicIdGroups
    .flat()
    .filter(isDefined)
    .forEach((id) => {
      normalizedKeyToId.set(String(id), id);
    });

  return Array.from(normalizedKeyToId.values());
};

/** Merges persisted Draft fields into the current Chat flow-state. */
export const mergeDraftIntoFlowState = ({ draft, flowState }) => ({
  ...(flowState || {}),
  ...(draft || {}),
  focusFlowId: null,
});

export const buildInitialSessionState = () => ({
  chatSessionId: null,
});

/**
 * Formats backend-provided capacity allocation dates for display.
 * @param {Object} options
 * @param {string|null|undefined} options.currentEndDatetime
 * @param {string|null|undefined} options.nextStartDatetime
 * @returns {string} The expiration and/or reset text, or an empty string when no dates are set.
 */
export const getCapacityDateText = ({ currentEndDatetime, nextStartDatetime }) => {
  const formattedCurrentEndDate = formatDateLabel(currentEndDatetime) || '';
  const formattedNextStartDate = formatDateLabel(nextStartDatetime) || '';

  if (formattedCurrentEndDate && formattedNextStartDate) {
    return `Expires: ${formattedCurrentEndDate} | Resets: ${formattedNextStartDate}`;
  }
  if (formattedNextStartDate) return `Resets: ${formattedNextStartDate}`;
  if (formattedCurrentEndDate) return `Expires: ${formattedCurrentEndDate}`;
  return '';
};

export const trackTopicCardAddRemove = ({
  action,
  chatSessionId,
  environmentState,
  flowId,
  sourceOfChange,
  topicId,
  topicName,
}) => {
  LogRocket.track('Flow_Topic_Card_Add_Remove', {
    add_vs_remove: action,
    chat_session_id: chatSessionId,
    environment_state: environmentState,
    ...(flowId != null ? { flow_id: flowId } : {}),
    source_of_change: sourceOfChange,
    topic_id: topicId,
    topic_name: topicName,
  });
};

export const trackNarrativeTemplateUsed = ({ activeTab, projectId, source, template }) => {
  try {
    LogRocket.track('Narrative_Template_Used', {
      'project id': projectId,
      source: source === 'workspace' ? 'Side window' : 'Full page',
      'template id': template?.id,
      'template name': template?.name,
      'template type': activeTab === 'custom' ? 'Custom' : 'Numerator',
    });
  } catch (error) {
    console.error('Failed to track narrative template use', error);
  }
};

export const getTemplateTopicIds = (template) => {
  if (Array.isArray(template?.topicIds)) return template.topicIds;
  if (Array.isArray(template?.topic_ids)) return template.topic_ids;
  return [];
};

/**
 * Extracts a user-facing delete error message from a template delete failure,
 * preferring the backend-provided message when present.
 * @param {Object} error - The rejected error (e.g. an axios error).
 * @returns {string} The backend message when available, otherwise a generic fallback.
 */
export const getDeleteTemplateErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData) && typeof responseData[0] === 'string') {
    return responseData[0];
  }

  return 'Unable to delete template. Please try again.';
};

export const getFlowTopicIds = (flowState) =>
  (Array.isArray(flowState?.topics) ? flowState.topics : [])
    .filter((topicId) => topicId != null)
    .map(String);

export const getFlowTopicsSignature = (flowState) => JSON.stringify(getFlowTopicIds(flowState));

export const getVariantSignature = (variant = {}, dimensions = []) => {
  const normalizedVariant = variant && typeof variant === 'object' ? variant : {};
  const signatureKeys =
    Array.isArray(dimensions) && dimensions.length > 0
      ? dimensions
      : Object.keys(normalizedVariant).sort();

  return JSON.stringify(
    signatureKeys.reduce((acc, key) => {
      if (key in normalizedVariant) {
        acc[key] = normalizedVariant[key];
      }
      return acc;
    }, {})
  );
};

export const buildTopicConfiguration = (group) => {
  const topics = Array.isArray(group?.topics) ? group.topics.filter(Boolean) : [];
  if (!group?.grouping || !topics.length) return null;

  const totalVariants = topics.length;
  const firstVariant = topics.find((topic) => topic?.variant)?.variant || {};
  const variantDimensions = Object.keys(firstVariant);
  const signatureDimensions = [...variantDimensions].sort();
  const variantOptions = variantDimensions.reduce((optionsByDimension, dimension) => {
    optionsByDimension[dimension] = Array.from(
      new Set(
        topics
          .map((topic) => topic?.variant?.[dimension])
          .filter((value) => typeof value === 'string' && value.length > 0)
      )
    );
    return optionsByDimension;
  }, {});
  const variantMap = topics.reduce((variantLookup, topic) => {
    variantLookup[getVariantSignature(topic?.variant, signatureDimensions)] = topic;
    return variantLookup;
  }, {});

  return {
    grouping: group.grouping,
    signatureDimensions,
    totalVariants,
    variantDimensions,
    variantMap,
    variantOptions,
  };
};

export const findFirstAvailableVariantSelection = ({
  config = {},
  occupiedTopicIdSet = new Set(),
  preferredVariant = null,
  previousTopicId = null,
} = {}) => {
  const variantDimensions = Array.isArray(config?.variantDimensions)
    ? config.variantDimensions
    : [];
  const signatureDimensions = Array.isArray(config?.signatureDimensions)
    ? config.signatureDimensions
    : variantDimensions;
  const variantMap = config?.variantMap || {};
  const variantOptions = config?.variantOptions || {};

  const isSelectionAvailable = (selectedVariant = {}) => {
    const selectedSignature = getVariantSignature(selectedVariant, signatureDimensions);
    const selectedTopic = variantMap?.[selectedSignature];
    if (selectedTopic?.id == null) return false;
    if (
      occupiedTopicIdSet?.has(String(selectedTopic.id)) &&
      String(selectedTopic.id) !== String(previousTopicId)
    ) {
      return false;
    }
    return true;
  };

  if (preferredVariant && isSelectionAvailable(preferredVariant)) {
    return preferredVariant;
  }

  const findSelection = (dimensionIndex, partialSelection) => {
    if (dimensionIndex >= variantDimensions.length) {
      return isSelectionAvailable(partialSelection) ? partialSelection : null;
    }

    const dimension = variantDimensions[dimensionIndex];
    const options = Array.isArray(variantOptions?.[dimension]) ? variantOptions[dimension] : [];

    for (const option of options) {
      const nextSelection = { ...partialSelection, [dimension]: option };
      const resolvedSelection = findSelection(dimensionIndex + 1, nextSelection);
      if (resolvedSelection) return resolvedSelection;
    }

    return null;
  };

  return findSelection(0, {}) || preferredVariant || {};
};

export const hasAvailableAlternativeVariant = ({
  config = {},
  occupiedTopicIdSet = new Set(),
  previousTopicId = null,
  selectedVariant = null,
} = {}) => {
  const signatureDimensions = Array.isArray(config?.signatureDimensions)
    ? config.signatureDimensions
    : [];
  const variantMap = config?.variantMap || {};
  const selectedSignature = getVariantSignature(selectedVariant, signatureDimensions);
  const selectedTopicId = variantMap?.[selectedSignature]?.id;
  const currentTopicId = selectedTopicId ?? previousTopicId;

  return Object.values(variantMap).some((topic) => {
    if (topic?.id == null) return false;
    if (String(topic.id) === String(currentTopicId)) return false;
    if (occupiedTopicIdSet?.has(String(topic.id)) && String(topic.id) !== String(previousTopicId)) {
      return false;
    }
    return true;
  });
};

export const buildTopicConfigurationLookup = (groups = []) =>
  (Array.isArray(groups) ? groups : []).reduce((acc, group) => {
    const topics = Array.isArray(group?.topics) ? group.topics.filter(Boolean) : [];
    if (!group?.grouping || !topics.length) return acc;

    const config = buildTopicConfiguration(group);
    if (!config) return acc;

    topics.forEach((topic) => {
      if (topic?.id == null) return;
      acc[String(topic.id)] = {
        config,
        id: topic.id,
        name: topic.name || null,
        selectedVariant: topic.variant || {},
        vizType: topic.vizType,
      };
    });

    return acc;
  }, {});

/**
 * Formats a date string to a label.
 * @param {string} isoDate - The ISO date string.
 * @returns {string|null} - The formatted date label or null if the date is invalid.
 */
const formatDateLabel = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Builds a flow data object for a draft flow state.
 * @param {Object} flowState - The flow state object.
 * @param {Array} componentIds - An array of component/topic IDs to include in the flow data, also updated with manual selections.
 * @returns {Object|null} - The flow data object or null if flowState is not provided.
 */
export const buildDraftFlowData = (flowState, componentIds) => {
  if (!flowState) return null;
  const narrativeName = flowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME;

  return {
    answers: {
      ...(flowState?.defaultPromptAnswers || {}),
      ...(flowState?.readOnlyPromptAnswers || {}),
      ...(flowState?.editablePromptAnswers || {}),
    },
    createdDatetime: formatDateLabel(flowState?.createdDatetime || flowState?.dateRangeStart),
    id: null,
    isReadOnly: true,
    name: narrativeName,
    narrative_id: flowState?.narrativeId || null,
    narrativeName,
    topic_ids: componentIds || null,
    topics: [],
  };
};

/**
 * Builds topic data for a draft component.
 * @param {Object} draft - The draft component object.
 * @returns {Object} - The topic data object.
 */
export const buildDraftTopicData = (draft) => {
  const resolvedTitle = draft?.cleanTitle || draft?.title;
  const hasDraftDisplayData = Boolean(draft?.dataset?.isDraftDisplayData);
  const dataset = draft?.dataset
    ? {
        ...draft.dataset,
        clean_title: resolvedTitle,
        readAs: draft.dataset?.readAs ?? draft.readAs,
        title: resolvedTitle,
        type: draft.dataset?.type,
      }
    : null;
  return {
    error: false,
    isHidden: false,
    loading: !hasDraftDisplayData,
    name: draft?.name || null,
    tags: {},
    title: resolvedTitle,
    topicId: null,
    variantSignature: draft?.variantSignature,
    visualization: {
      clean_title: resolvedTitle,
      datasets: dataset ? [dataset] : [],
      sample_size: draft?.sampleSize || null,
    },
  };
};

/**
 * Builds a topic shell object for a draft component.
 * @param {Object} draft - The draft component object.
 * @returns {Object} - The topic shell object.
 */
export const buildTopicShell = (draft) => ({
  feedback: {},
  id: draft.componentId,
  isHidden: false,
  name: draft.name || null,
  status: narrativeStatus.COMPLETED,
  tags: {},
  title: draft.title,
});

export const buildPromptsFromFlowState = (flowState) => {
  const requirements = Array.isArray(flowState?.promptRequirements)
    ? flowState.promptRequirements
    : [];
  const promptGrouping = Array.isArray(flowState?.promptGrouping) ? flowState.promptGrouping : [];

  const normalizedPrompts = requirements.map((prompt, index) => ({
    ...prompt,
    emptyValue: prompt?.emptyValue || `Add ${prompt?.name || DEFAULT_PROMPT_NAME}`,
    id: prompt?.id || `prompt-${index}`,
    name: prompt?.name || DEFAULT_PROMPT_NAME,
    promptKey: prompt?.promptKey || prompt?.id || `prompt-${index}`,
    shortTitle: prompt?.shortTitle || prompt?.name || DEFAULT_PROMPT_NAME,
    title: prompt?.title || prompt?.name || DEFAULT_PROMPT_NAME,
  }));

  return filterPrompts(normalizedPrompts, promptGrouping);
};

export const extractEditablePromptAnswers = (flowState) => {
  if (!flowState || typeof flowState !== 'object') return {};
  const rawAnswers = {
    ...(flowState?.defaultPromptAnswers || {}),
    ...(flowState?.editablePromptAnswers || {}),
  };
  if (!rawAnswers || typeof rawAnswers !== 'object') return {};
  return Object.keys(rawAnswers).reduce((acc, key) => {
    const value = rawAnswers[key];
    acc[key] = Array.isArray(value) ? [...value] : value;
    return acc;
  }, {});
};

export const hasPromptRequirements = (flowState) => {
  return Array.isArray(flowState?.promptRequirements)
    ? flowState.promptRequirements.length > 0
    : false;
};

export const getRunTopicComponentId = (topic = {}) =>
  topic?.topicId ?? topic?.topic_id ?? topic?.componentId ?? topic?.component_id ?? null;

export const areRequiredPromptsFilled = (prompts, transformedAnswersMap) => {
  if (!Array.isArray(prompts) || prompts.length === 0) return true;

  const requiredPrompts = prompts.filter(
    (prompt) => prompt.isRequired || prompt.isRequiredForModal
  );

  if (!requiredPrompts.length) return true;

  return requiredPrompts.every((prompt) => {
    const answers = transformedAnswersMap?.[prompt.promptKey];
    return Array.isArray(answers) ? answers.length > 0 : Boolean(answers);
  });
};

/**
 * Validates whether the selected topic card count has reached the maximum and shows an error toast.
 * @param {number} topicCardCount
 * @returns {boolean} true when the max has been reached (and insertion should be blocked)
 */
export const checkIsTopicCardCountMax = (topicCardCount) => {
  if (topicCardCount >= MAX_TOPIC_CARDS) {
    toast.error(
      `A narrative can contain up to ${MAX_TOPIC_CARDS} topic cards. Remove a topic card from the canvas before adding a new one.`
    );
    return true;
  }
  return false;
};

/** Gets appropriate icon for topic library card based on viz type
 * @param {string} vizType
 * @returns {JSX.Element | null}
 */

export const getTopicLibraryCardIcon = (vizType) => {
  switch (vizType) {
    case 'bar-chart':
      return <VizHorizontalBar />;
    case 'bar-chart-two-labels':
      return <VizGroupedHorizontalBar />;
    case 'bar-stack-chart':
      return <VizStackedHorizontalBar />;
    case 'bar-tornado-chart':
      return <VizTornado />;
    case 'brand-tree-v2':
    case 'retailer-leakage-tree':
      return <VizTree />;
    case 'card-blocks':
      return <VizSummaryCards />;
    case 'column-chart':
      return <VizVerticalBar />;
    case 'ebsova-tree':
      return <VizSimpleTree />;
    case 'grouped-vertical-bar-chart':
      return <VizGroupedVerticalBar />;
    case 'incrementality-source-of-volume':
      return <VizTree />;
    case 'line-chart':
      return <VizLineChart />;
    case 'scatter-chart':
      return <VizScatter />;
    case 'summary-card':
      return <VizSummaryCards />;
    case 'table':
      return <VizTable />;
    default:
      return null;
  }
};

/**
 * Validates a chat message input value and returns an object with validation results.
 * @param {string} inputValue
 * @returns {{ hasInputText: boolean, isInputTooLong: boolean, isValid: boolean, trimmedValue: string }}
 */
export const getChatMessageValidation = (inputValue = '') => {
  const trimmedValue = inputValue?.trim() || '';
  const isInputTooLong = trimmedValue.length > MAX_CHAT_MESSAGE_LENGTH;

  return {
    hasInputText: Boolean(trimmedValue),
    isInputTooLong,
    isValid: Boolean(trimmedValue) && !isInputTooLong,
    trimmedValue,
  };
};
